/**
 * Founder bug 2026-10-20 — Activity and Security must agree.
 *
 * A small coral chip rendered next to a sender's name wherever Activity
 * shows a visit that Security has already flagged (threat_alert) or
 * blocked on this site. Clicking the chip opens the sender in
 * Security › Attackers (`?view=security&section=attackers&threat=<ref>`
 * when a threat_alert id is available, otherwise just the Attackers tab
 * for the active site).
 *
 * Copy: "Flagged" for a threat_alert match (founder 2026-10-21: shortened
 * from "Flagged in Security" — the row read as cramped; the tooltip still
 * names Security as the owner), "Blocked" when the blocklist has an active
 * entry (block takes precedence; a blocked sender is already past
 * "flagged"). Low-tone — never shouts; the row keeps its primary identity
 * glyphs and marks.
 */

import { ShieldAlert } from 'lucide-react';

import type { SecurityFlag } from '@sill/types';

import type { SetView } from '../../app/view-router.js';
import { Tooltip } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";

export interface SecurityFlagChipProps {
  readonly flag: SecurityFlag;
  readonly setView?: SetView;
  /** Row vs header surface — the header renders a slightly bigger pill. */
  readonly surface?: 'row' | 'header';
}

/**
 * Pure-rendering helper split out for `visit-identity.ts` + tests: given
 * a `SecurityFlag`, return the label the chip should show.
 */
// Founder 2026-10-21: "Flagged in Security" ran wide enough on the row
// to look cramped next to the identity marks. The pill now shows just
// "Flagged"; the tooltip (`securityFlagChipTooltip`) still explains that
// Security owns the flag and links to Attackers.
export function securityFlagChipLabel(flag: SecurityFlag): string {
  return flag.kind === 'blocked' ? 'Blocked' : 'Flagged';
}

/**
 * Pure-rendering helper for the chip's hover + aria-label — one plain
 * sentence that names what the chip proves ("Security has flagged this
 * sender on this site").
 */
export function securityFlagChipTooltip(flag: SecurityFlag): string {
  return flag.kind === 'blocked'
    ? 'This sender is on your Security blocklist for this site.'
    : 'Security has flagged this sender on this site. Click to open it in Attackers.';
}

export function SecurityFlagChip({
  flag,
  setView,
  surface = 'row',
}: SecurityFlagChipProps): JSX.Element {
  const C = useC();
  const label = securityFlagChipLabel(flag);
  const tooltip = securityFlagChipTooltip(flag);
  const onClick = (e: React.MouseEvent | React.KeyboardEvent): void => {
    e.stopPropagation();
    if (setView === undefined) return;
    const opts: Parameters<SetView>[1] = { security_section: 'attackers' };
    if (flag.threat_ref !== null && flag.threat_ref.length > 0) {
      opts.threat = flag.threat_ref;
    }
    setView('security', opts);
  };
  return (
    <Tooltip content={tooltip} side="bottom">
      <button
        type="button"
        data-testid="security-flag-chip"
        data-flag-kind={flag.kind}
        {...(flag.threat_ref !== null ? { 'data-threat-ref': flag.threat_ref } : {})}
        className="inline-flex items-center gap-1"
        onClick={onClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); } }}
        aria-label={tooltip}
        style={{
          background: 'transparent',
          border: `1px solid ${C.coralFg}`,
          borderRadius: 2,
          color: C.coralFg,
          cursor: setView !== undefined ? 'pointer' : 'default',
          fontFamily: monoFont,
          fontSize: surface === 'header' ? 10 : 9,
          letterSpacing: 1,
          lineHeight: 1.4,
          padding: surface === 'header' ? '2px 6px' : '1px 5px',
          textTransform: 'uppercase',
          flexShrink: 0,
        }}
      >
        <ShieldAlert size={surface === 'header' ? 11 : 10} color={C.coralFg} aria-hidden="true" />
        {label}
      </button>
    </Tooltip>
  );
}
